import { Injectable } from '@angular/core';
import { BullhornEntityTypes, BullhornNamespace } from 'datasource-service-lib';
import {
  EntityType,
  IBullhornCandidate,
  IBullhornClientContact,
  IBullhornClientCorporation,
  IBullhornJobOrder,
  IBullhornOpportunity,
  IBullhornPlacement,
  IDataSourceApiService,
  IEntity,
  IEntityPickerService,
} from 'interfaces-lib';
import { IIconService } from 'interfaces-lib';
import { IPickerInfo } from 'interfaces-lib';
import { Observable, zip } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';

interface ISearchEntity {
  type: BullhornEntityTypes;
  queryFields: string[];
  mapFn(INamedEntity: IEntity): IPickerInfo;
  cast(IEntity: IEntity): IEntity;
}

@Injectable({ providedIn: 'root' })
export class BullhornEntityPickerService implements IEntityPickerService {
  private apiService: IDataSourceApiService;
  private iconService: IIconService;

  private readonly searchEntityTypes: ISearchEntity[] = [
    {
      type: BullhornEntityTypes.Candidate,
      queryFields: ['firstName', 'lastName', 'name', 'email'],
      mapFn: this.candidateMapper.bind(this),
      cast: (e: IEntity) => BullhornEntityPickerService.checkedCast<IBullhornCandidate>(e),
    },
    {
      type: BullhornEntityTypes.ClientCorporation,
      queryFields: ['name'],
      mapFn: this.clientCorporationMapper.bind(this),
      cast: (e: IEntity) => e as IBullhornClientCorporation,
    },
    {
      type: BullhornEntityTypes.JobOrder,
      queryFields: ['title', 'clientCorporation.name', 'clientContact.name'],
      mapFn: this.jobOrderMapper.bind(this),
      cast: (e: IEntity) => e as IBullhornJobOrder,
    },
    {
      type: BullhornEntityTypes.Placement,
      queryFields: ['jobOrder.title', 'candidate.name', 'clientCorporation.name', 'clientContact.name'],
      mapFn: this.placementMapper.bind(this),
      cast: (e: IEntity) => e as IBullhornPlacement,
    },
    {
      type: BullhornEntityTypes.ClientContact,
      queryFields: ['firstName', 'lastName', 'name', 'email'],
      mapFn: this.clientContactMapper.bind(this),
      cast: (e: IEntity) => e as IBullhornClientContact,
    },
    {
      type: BullhornEntityTypes.Opportunity,
      queryFields: ['title'],
      mapFn: this.opportunityMapper.bind(this),
      cast: (e: IEntity) => e as IBullhornOpportunity,
    },
  ];

  // TODO: BW - we would need to make BullhornDataSourceApiService injectable
  // constructor(private apiService: BullhornDataSourceApiService) {
  constructor(sps: SharedServiceProviderServiceFactory) {
    const serviceProvider = sps.getServiceProviderService(BullhornNamespace.name);
    this.apiService = serviceProvider.getDataSourceApiService();
    this.iconService = serviceProvider.getIconService();
  }

  findMatches(searchTerm: string, searchTypes?: EntityType[]): Observable<IPickerInfo[]> {
    const obs: Observable<IPickerInfo[]>[] = [];
    this.searchEntityTypes
      .filter((searchEntity) => !searchTypes || searchTypes.includes(searchEntity.type))
      .forEach((entityType) => {
        const query$ = this.apiService.query(entityType.type, { searchTerm, count: 20, queryFields: entityType.queryFields });
        const mapper$ = query$.pipe(map((entities) => entities.map((entity) => entityType.mapFn(entityType.cast(entity)))));
        obs.push(mapper$);
      });

    const queries$: Observable<IPickerInfo[][]> = zip(...obs);

    return queries$.pipe(
      map((resultsByEntityType: IPickerInfo[][]) => {
        const flat: any[] = [];
        resultsByEntityType.forEach((r) => flat.push(...r));

        flat.sort((a: IPickerInfo, b: IPickerInfo) => {
          return this.checkRelevance(b.label, searchTerm) - this.checkRelevance(a.label, searchTerm);
        });

        return flat;
      })
    );
  }

  private checkRelevance(str: string, searchTerm: string): number {
    return str.toLowerCase().includes(searchTerm.toLowerCase()) ? 1 : 0;
  }

  // TODO: BW - maybe add a 2nd argument and check the types match?
  private static checkedCast<T extends IEntity>(e: IEntity): T {
    return e as T;
  }

  private candidateMapper(e: IBullhornCandidate): IPickerInfo {
    return {
      entity: { ...e, name: `${e.firstName} ${e.lastName}` },
      icon: this.iconService.getIcon(e.type),
      label: `${e.firstName} ${e.lastName}`,
      showId: true,
      details: [e.companyName, e.occupation].filter((p) => p).join(', '),
    };
  }

  private clientCorporationMapper(e: IBullhornClientCorporation): IPickerInfo {
    return {
      entity: e,
      icon: this.iconService.getIcon(e.type),
      label: `${e.name}`,
      showId: true,
    };
  }

  private jobOrderMapper(e: IBullhornJobOrder): IPickerInfo {
    return {
      entity: { ...e, name: e.title || '' }, // force to match the INamedEntity interface for file-list-view to work
      icon: this.iconService.getIcon(e.type),
      label: `${e.title ? e.title : ''}`,
      showId: true,
      details: `${e.clientCorporation.name}`,
    };
  }

  private placementMapper(e: IBullhornPlacement): IPickerInfo {
    const candidateName = [e.candidate.firstName, e.candidate.lastName].filter((p) => p).join(' ');
    let placementLabel = `Placement`;
    if (candidateName) {
      if (e.jobOrder.title) {
        placementLabel = `${candidateName} - ${e.jobOrder.title}`;
      } else {
        placementLabel = `${candidateName}`;
      }
    } else if (e.jobOrder.title) {
      placementLabel = `${e.jobOrder.title}`;
    }

    return {
      entity: {
        ...e,
        name: `${e.candidate.firstName} ${e.candidate.lastName} - ${e.jobOrder.title}`,
      }, // force to match the INamedEntity interface for file-list-view to work
      icon: this.iconService.getIcon(e.type),
      label: placementLabel,
      showId: true,
      details: [e.clientCorporation.name, candidateName].filter((p) => p).join(', '),
    };
  }
  private clientContactMapper(e: IBullhornClientContact): IPickerInfo {
    return {
      entity: e,
      icon: this.iconService.getIcon(e.type),
      label: `${e.name}`,
      showId: true,
      details: `${e.clientCorporation.name}`,
    };
  }
  private opportunityMapper(e: IBullhornOpportunity): IPickerInfo {
    return {
      entity: e,
      icon: this.iconService.getIcon(e.type),
      label: e.title ? `${e.title}` : '',
      showId: true,
      details: `${e.clientCorporation.name}`,
    };
  }
}
